import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { AUTH } from "../constants/testIds";

export default function SignUp() {
    const { signup } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = React.useState({ full_name: "", email: "", phone: "", password: "" });
    const [loading, setLoading] = React.useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            await signup(form);
            toast.success("Account created. Let's add your children.");
            navigate("/dashboard");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Sign-up failed");
        } finally { setLoading(false); }
    }

    return (
        <main className="min-h-[calc(100vh-5rem)] flex items-center py-16">
            <div className="max-w-5xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-12 items-center w-full">
                <div className="hidden md:block">
                    <div className="overline text-brand-stone mb-4">Enroll</div>
                    <h1 className="font-serif text-5xl leading-tight text-brand-ink">
                        Begin your child&apos;s <br /><span className="italic text-primary">Sunday journey.</span>
                    </h1>
                    <p className="mt-6 text-brand-stone max-w-md">
                        Create one family account for all your children. Register once, and your children
                        are placed automatically in the right class with the right teacher.
                    </p>
                    <ul className="mt-8 space-y-3 text-sm text-brand-ink/80">
                        <li>• One account · unlimited children</li>
                        <li>• Auto-placement by grade and age</li>
                        <li>• Secure card payments via Square</li>
                    </ul>
                </div>
                <Card className="border-border">
                    <CardContent className="p-8">
                        <form onSubmit={onSubmit} className="space-y-4">
                            <div className="mb-4">
                                <div className="overline text-brand-stone">Create your account</div>
                                <h2 className="font-serif text-2xl mt-1 text-brand-ink">Parent registration</h2>
                            </div>
                            <div>
                                <Label htmlFor="name">Full name</Label>
                                <Input id="name" required placeholder="Aisha" value={form.full_name}
                                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                    data-testid={AUTH.signupName} className="mt-1.5" />
                            </div>
                            <div>
                                <Label htmlFor="email">Email</Label>
                                <Input id="email" type="email" required placeholder="you@email.com" value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    data-testid={AUTH.signupEmail} className="mt-1.5" />
                            </div>
                            <div>
                                <Label htmlFor="phone">Phone</Label>
                                <Input id="phone" value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    data-testid={AUTH.signupPhone} className="mt-1.5" placeholder="(253) 555-0123" />
                            </div>
                            <div>
                                <Label htmlFor="password">Password</Label>
                                <Input id="password" type="password" required value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    data-testid={AUTH.signupPassword} className="mt-1.5" placeholder="At least 6 characters" />
                            </div>
                            <Button type="submit" disabled={loading} data-testid={AUTH.signupSubmit}
                                className="w-full bg-primary hover:bg-primary/90 h-11">
                                {loading ? "Creating…" : "Create account"}
                            </Button>
                            <p className="text-sm text-center text-brand-stone">
                                Already registered? <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
                            </p>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </main>
    );
}
